import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Zap, ShieldCheck, LineChart, Sparkles, TrendingUp, TrendingDown, ArrowRight } from 'lucide-react';

const TICKER_SYMBOLS = [
  { s: 'BTC/USD',  base: 68222.15,  vol: 145,   px: 2 },
  { s: 'ETH/USD',  base: 3831.40,   vol: 8,     px: 2 },
  { s: 'XAU/USD',  base: 2412.55,   vol: 1.4,   px: 2 },
  { s: 'EUR/USD',  base: 1.08212,   vol: 0.001, px: 5 },
  { s: 'GBP/USD',  base: 1.26550,   vol: 0.001, px: 5 },
  { s: 'USD/JPY',  base: 149.822,   vol: 0.09,  px: 3 },
  { s: 'NAS100',   base: 18654.9,   vol: 22,    px: 1 },
  { s: 'SPX500',   base: 5205.10,   vol: 6,     px: 2 },
  { s: 'SOL/USD',  base: 172.85,    vol: 0.9,   px: 2 },
  { s: 'AAPL',     base: 232.48,    vol: 0.7,   px: 2 },
  { s: 'NVDA',     base: 138.92,    vol: 1.2,   px: 2 },
  { s: 'TSLA',     base: 258.72,    vol: 2.1,   px: 2 }
];

const useAnimatedTicker = () => {
  const [data, setData] = useState(() => TICKER_SYMBOLS.map(t => ({
    ...t,
    price: t.base,
    changePct: (Math.random() - 0.4) * 2
  })));

  useEffect(() => {
    const iv = setInterval(() => {
      setData(prev => prev.map(t => {
        const noise = (Math.random() - 0.5) * t.vol;
        const newPrice = Math.max(0.0001, t.price + noise);
        const delta = ((newPrice - t.base) / t.base) * 100;
        return { ...t, price: newPrice, changePct: delta };
      }));
    }, 900);
    return () => clearInterval(iv);
  }, []);

  return data;
};

const MockChart = () => {
  const [seed, setSeed] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setSeed(s => s + 1), 2200);
    return () => clearInterval(iv);
  }, []);
  const points = Array.from({ length: 48 }, (_, i) => {
    const x = i * 10;
    const base = 60;
    const wave = Math.sin(i * 0.35 + seed * 0.3) * 18 + Math.cos(i * 0.6 + seed * 0.2) * 9;
    return `${x},${base - wave}`;
  }).join(' ');
  return (
    <svg viewBox="0 0 480 120" className="w-full h-full" preserveAspectRatio="none">
      <defs>
        <linearGradient id="hero-grad" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#00e676" stopOpacity="0.45"/>
          <stop offset="100%" stopColor="#00e676" stopOpacity="0"/>
        </linearGradient>
      </defs>
      <polyline points={points} fill="none" stroke="#00e676" strokeWidth="1.5" style={{ transition: 'all 1.2s ease' }}/>
      <polygon points={`${points} 480,120 0,120`} fill="url(#hero-grad)" style={{ transition: 'all 1.2s ease' }}/>
    </svg>
  );
};

const DeviceMockup = ({ tickers }) => {
  const featured = tickers.slice(0, 8);
  const eur = tickers.find(t => t.s === 'EUR/USD') || tickers[3];
  return (
    <div className="relative w-full max-w-[640px]">
      <div className="relative rounded-2xl overflow-hidden border border-[#1a2823] bg-gradient-to-br from-[#0a1210] to-[#050807] shadow-2xl green-glow">
        <div className="flex items-center gap-1.5 px-4 py-2 bg-[#0a1210] border-b border-[#1a2823]">
          <div className="w-2 h-2 rounded-full bg-red-500"/>
          <div className="w-2 h-2 rounded-full bg-yellow-500"/>
          <div className="w-2 h-2 rounded-full bg-[#00e676]"/>
          <span className="ml-3 text-[10px] text-gray-400 mono">NEXUS FX PRO · cTrader Web</span>
          <span className="ml-auto text-[10px] text-[#00e676] mono flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00e676] pulse-dot"/> LIVE
          </span>
        </div>
        <div className="grid grid-cols-[130px_1fr] h-[320px]">
          <div className="border-r border-[#1a2823] p-2 space-y-1.5 overflow-hidden">
            {featured.map(t => (
              <div key={t.s} className="flex justify-between items-center text-[10px]">
                <span className="text-gray-400 mono">{t.s}</span>
                <span className={`mono ${t.changePct >= 0 ? 'text-[#00e676]' : 'text-red-400'}`}>
                  {t.price.toFixed(t.px)}
                </span>
              </div>
            ))}
          </div>
          <div className="relative p-3">
            <div className="flex justify-between items-center mb-2">
              <div>
                <div className="text-xs text-gray-400">{eur.s} · H1</div>
                <div className="text-lg font-bold text-white mono">
                  {eur.price.toFixed(eur.px)}{' '}
                  <span className={`text-xs ${eur.changePct >= 0 ? 'text-[#00e676]' : 'text-red-400'}`}>
                    {eur.changePct >= 0 ? '+' : ''}{eur.changePct.toFixed(2)}%
                  </span>
                </div>
              </div>
              <div className="flex gap-2">
                <button className="px-3 py-1 bg-red-500/20 text-red-400 rounded text-xs font-semibold">SELL</button>
                <button className="px-3 py-1 bg-[#00e676]/20 text-[#00e676] rounded text-xs font-semibold">BUY</button>
              </div>
            </div>
            <div className="h-[220px]"><MockChart/></div>
            <div className="absolute bottom-2 left-3 right-3 flex justify-between text-[9px] text-gray-500 mono">
              <span>14:00</span><span>15:00</span><span>16:00</span><span>17:00</span>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute -bottom-6 -right-6 w-[150px] rounded-2xl border border-[#1a2823] bg-[#0a1210] p-3 shadow-xl hidden md:block">
        <div className="text-[10px] text-gray-400">Portfolio</div>
        <div className="text-lg font-bold text-white mono">$42,860.12</div>
        <div className="mt-2 h-10"><MockChart/></div>
        <div className="flex gap-1 mt-2">
          <div className="flex-1 bg-red-500 text-white text-[9px] text-center py-1 rounded font-semibold">Sell</div>
          <div className="flex-1 bg-[#00e676] text-black text-[9px] text-center py-1 rounded font-semibold">Buy</div>
        </div>
      </div>
    </div>
  );
};

const MarqueeTicker = ({ tickers }) => (
  <div className="relative overflow-hidden border-y border-[#1a2823] bg-[#050807]/70 backdrop-blur">
    <div className="flex whitespace-nowrap animate-[marquee_45s_linear_infinite] py-2.5">
      {[...tickers, ...tickers].map((t, i) => (
        <div key={`${t.s}-${i}`} className="flex items-center gap-2 px-6 shrink-0">
          <span className="text-xs font-semibold text-gray-300 mono">{t.s}</span>
          <span className="text-xs text-white mono">{t.price.toFixed(t.px)}</span>
          <span className={`text-[11px] font-semibold mono flex items-center gap-0.5 ${t.changePct >= 0 ? 'text-[#00e676]' : 'text-red-400'}`}>
            {t.changePct >= 0 ? <TrendingUp size={10}/> : <TrendingDown size={10}/>}
            {t.changePct >= 0 ? '+' : ''}{t.changePct.toFixed(2)}%
          </span>
        </div>
      ))}
    </div>
    <style>{`@keyframes marquee { from { transform: translateX(0);} to { transform: translateX(-50%);} }`}</style>
  </div>
);

const Hero = () => {
  const tickers = useAnimatedTicker();

  return (
    <>
      <section className="relative hero-glow pt-24 sm:pt-32 pb-10 sm:pb-12 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40"/>
        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
          <div className="fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00e676]/10 border border-[#00e676]/30 text-[#00e676] text-[11px] font-semibold mb-5 tracking-wider">
              <Sparkles size={12}/> NEXUS FX PRO · MULTI-ASSET · 24/7
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold leading-[1.05] sm:leading-[1.02] text-white">
              Where Serious<br/>Traders Sharpen<br/>Their <span className="text-[#00e676] text-glow">Edge.</span>
            </h1>
            <p className="mt-4 sm:mt-6 text-base sm:text-lg text-gray-400 max-w-lg">
              Institutional-grade execution on <span className="text-white font-semibold">1,200+ instruments</span> — Forex, Crypto, Stocks, Metals & Indices — from one lightning-fast terminal.
            </p>
            <div className="mt-6 sm:mt-8 flex flex-wrap items-center gap-3 sm:gap-4">
              <Link to="/dashboard" className="btn-green px-6 sm:px-8 py-3 sm:py-3.5 rounded-full font-semibold text-sm sm:text-base inline-flex items-center gap-2 group">
                Launch Terminal <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform"/>
              </Link>
              <Link to="/login" className="btn-outline-green px-5 sm:px-6 py-3 sm:py-3.5 rounded-full font-semibold text-xs sm:text-sm">
                Open Free Account
              </Link>
            </div>
            <div className="mt-8 sm:mt-10 grid grid-cols-3 gap-3 sm:gap-6 max-w-xl">
              {[
                { icon: Zap, label: 'Sub-second\nexecution' },
                { icon: ShieldCheck, label: 'Segregated\nfunds' },
                { icon: LineChart, label: 'Pro-grade\nanalytics' }
              ].map((f, i) => (
                <div key={f.label} className="flex items-start gap-2 sm:gap-3">
                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-[#00e676]/10 border border-[#00e676]/30 flex items-center justify-center text-[#00e676] shrink-0">
                    <f.icon size={14}/>
                  </div>
                  <div className="text-xs sm:text-sm text-gray-300 whitespace-pre-line leading-tight">{f.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <DeviceMockup tickers={tickers}/>
          </div>
        </div>
      </section>
      <MarqueeTicker tickers={tickers}/>
    </>
  );
};

export default Hero;
